import {Text, Title} from "components/common/Formatting";
import apiData from "lib/stores/apiData";
import mapData from "lib/stores/mapData";
import context from "lib/stores/context";
import styles from "./index.module.scss";
import classNames from "classnames";
import SummaryStatistic from "./SummaryStatistic";
import {useSnapshot} from "valtio";
import RadixSelect from "../../common/RadixSelect";
import dictMap from "../../../lib/helpers/dictMap";

const CountrySummaryContent = () => {
  const {countries, metadata} = useSnapshot(apiData);
  const {activeCountry, activeRegion, mode} = useSnapshot(context);
  const {year} = useSnapshot(mapData, {sync: true});

  const formatGDP = () => {
    if (!countries[activeCountry][year].gdp) {
      return "-";
    }
    return `$${parseInt(countries[activeCountry][year]?.gdp)?.toLocaleString()}` || "-";
  };

  const formatPopulation = () => {
    if (!countries[activeCountry][year].population) {
      return "-";
    }
    return countries[activeCountry][year].population?.toLocaleString();
  };

  const formatValue = (value, type = "") => {
    if (!value) {
      return "-";
    }
    if (type === "prevalence") {
      return Math.round(value * 10) / 10;
    } else {
      return Math.round(value);
    }
  };

  const getCountryOptions = () => {
    return dictMap(countries, (country) => {
      return {
        // label: `${country.name} (${country.iso})`,
        label: `${country.name}`,
        value: country.iso,
      };
    }).sort((a, b) => {
      if (a.name < b.name) return -1;
      return 1;
    });
  };

  const setSelectedOption = (value: string) => {
    const hash = window.location.hash.slice(1);
    const sections = hash.split(':');

    const newSections = sections.map((section) => {
      if (section.startsWith('country=')) {
        return 'country=' + value;
      }
      return section;
    });

    window.location.hash = '#' + newSections.join(':');
  };

  const renderExploreButtonCountrySwitcher = () => {
    if (mode === "data") {
      return <RadixSelect
        onValueChange={setSelectedOption}
        value={activeCountry}
        triggerClassName={styles["summary-country-switcher-styles"] + " pointer-events-auto "}
        selectContentClassName="-translate-x-[2px] max-h-[800px] md:w-64 lg:w-48 xl:w-64"
        selectContentWrapperClassName="md:-translatex-0 -translate-x-[5px]"
        className=""
        placeholder={"Switch Country"}
        groups={[
          {
            label: "Switch Country",
            items: getCountryOptions(),
          },
        ]}
      />
    } else {
      {/* Explore Button */
      }
      return <button
        type="button"
        className={classNames(styles["prevalence-button"], styles["summary-content-button-root-styles"], styles["is-variant-explore"])}
        onClick={() => {
          window.location.hash = countries[activeCountry]?.explore_country_content_url ?? `#mode=data:country=${activeCountry}:dimension=p`;
          window.history.pushState(null, null, window.location.href);
        }}
      >
        Explore Country Data
      </button>
    }
  }

  return (
    <div className={classNames(styles["country-summary-content-container"])}>
      <div className={classNames(styles["country-summary-content-title-stats-wrapper"])}>
        <Title className={classNames(styles["country-summary-title"], "!uppercase")} element="h1" variant="lg">
          {countries[activeCountry].name}
        </Title>
        <PopGDPStatsRow pop={formatPopulation()} gdp={formatGDP()}/>
      </div>
      {/* Summary Content Body */}
      <div className={styles["summary-statistics-row"]}>
        <SummaryStatistic
          label="Prevalence"
          value={formatValue(countries[activeCountry][year]?.prevalence, "prevalence")}
          content={metadata.labels.prevalence_tooltip}
        />
        <SummaryStatistic
          label="Vulnerability"
          value={formatValue(countries[activeCountry][year]?.vulnerability)}
          content={metadata.labels.vulnerability_tooltip}
        />
        <SummaryStatistic
          label="Gov Response"
          value={formatValue(countries[activeCountry][year]?.govresponse)}
          content={metadata.labels.gov_response_tooltip}
        />
      </div>
      {renderExploreButtonCountrySwitcher()}
    </div>
  );
};

// ╭──────────────────────────────────────────────────────────╮
// │ Population and GDP Statistics Row                        │
// ╰──────────────────────────────────────────────────────────╯
const PopGDPStatsRow = ({pop, gdp}) => {
  return (
    <Text className={classNames(styles["summary-pop-gdp-stats"])} element="ul" variant="md">
      <li className={styles.statistic}>
        <strong>Population</strong>
        <span>{pop}</span>
      </li>
      <li className={styles.statistic}>
        <strong>GDP (PPP)</strong>
        <span>{gdp}</span>
      </li>
    </Text>
  );
};

export default CountrySummaryContent;
